import { BaseModel, type RelationConfig } from './base';

import { Employee } from './employee';
import { EmployeeHour, EmployeeHourType, type EmployeeHourRosterFrequency, type EmployeeHourRosterWeek } from './employeeHour';
import { EmployeeBreak } from './employeeBreak';
import { DateYMD, dateYMDRelation } from '../lib/dates';

/** Hydrated roster response returned by the week and month endpoints. */
export class Roster extends BaseModel {
  date!: DateYMD;
  dates!: RosterDate[];
  rosters!: EmployeeRoster[];
  dateLabel!: string;

  static override relations(): RelationConfig {
    return {
      date: dateYMDRelation,
      dates: { type: RosterDate },
      rosters: { type: EmployeeRoster },
    };
  }
}

export class RosterDate extends BaseModel {
  date!: DateYMD;
  inMonth!: boolean;

  static override relations(): RelationConfig {
    return {
      date: dateYMDRelation,
    };
  }
}

export class EmployeeRoster extends BaseModel {
  employee!: Employee;
  dates!: EmployeeRosterCurrentDay[];
  currentWeek!: EmployeeRosterCurrentDay[];

  static override relations(): RelationConfig {
    return {
      employee: { type: Employee },
      dates: { type: EmployeeRosterCurrentDay },
      currentWeek: { type: EmployeeRosterCurrentDay },
    };
  }
}

/**
 * EmployeeRosterCurrentDay - represents a single day in an employee's current roster.
 * Returned as part of the roster week/date queries.
 */
export class EmployeeRosterCurrentDay extends BaseModel {
  idEmployee!: number;
  date!: DateYMD;
  hour!: EmployeeHour;
  type!: EmployeeHourType;
  breaks!: EmployeeBreak[];
  employee!: string;
  status!: number;

  static override relations(): RelationConfig {
    return {
      date: dateYMDRelation,
      hour: { type: EmployeeHour },
      breaks: { type: EmployeeBreak },
    };
  }

  get isDefaultHours(): boolean {
    return this.type === EmployeeHourType.default;
  }

  get isOverrideHours(): boolean {
    return this.type === EmployeeHourType.override;
  }
}

/**
 * RosterEmployeeDefault - the main roster configuration for an employee.
 * Returned by GET /api/v2/roster/employeeDefault?employee={id}
 */
export class RosterEmployeeDefault extends BaseModel {
  frequency!: EmployeeHourRosterFrequency;
  weeks!: RosterEmployeeDefaultWeek[];
  frequencies!: RosterEmployeeFrequency[];
  employee!: Employee;

  static override relations(): RelationConfig {
    return {
      employee: { type: Employee },
      weeks: { type: RosterEmployeeDefaultWeek },
      frequencies: { type: RosterEmployeeFrequency },
    };
  }

  getWeek(week: EmployeeHourRosterWeek): RosterEmployeeDefaultWeek | null {
    for (const defaultWeek of this.weeks) {
      if (defaultWeek.week === week) {
        return defaultWeek;
      }
    }
    return null;
  }

  get isRosterFrequencyWeekly(): boolean {
    return this.frequency === 1; // EmployeeHourRosterFrequency.weekly
  }

  get isRosterFrequencyFortnightly(): boolean {
    return this.frequency === 2; // EmployeeHourRosterFrequency.fortnightly
  }

  get isRosterFrequencyMonthly(): boolean {
    return this.frequency === 4; // EmployeeHourRosterFrequency.monthly
  }
}

/**
 * RosterEmployeeFrequency - a frequency option (weekly/fortnightly/monthly)
 * with its available weeks. Properties like `currentWeeks`, `weekLabel`,
 * `dateLabel`, `label` come from the API response.
 */
export class RosterEmployeeFrequency extends BaseModel {
  frequency!: EmployeeHourRosterFrequency;
  label!: string;
  currentWeeks!: RosterEmployeeFrequencyWeek[];

  static override relations(): RelationConfig {
    return {
      currentWeeks: { type: RosterEmployeeFrequencyWeek },
    };
  }
}

/**
 * RosterEmployeeFrequencyWeek - a week option within a frequency.
 * Properties like `weekLabel`, `dateLabel`, `week` come from the API response.
 */
export class RosterEmployeeFrequencyWeek extends BaseModel {
  week!: number;
  weekLabel!: string;
  dateLabel!: string;
}

/**
 * RosterEmployeeDefaultWeek - a week in the default roster.
 */
export class RosterEmployeeDefaultWeek extends BaseModel {
  week!: number;
  days!: RosterEmployeeDefaultDay[];

  static override relations(): RelationConfig {
    return {
      days: { type: RosterEmployeeDefaultDay },
    };
  }
}

/**
 * RosterEmployeeDefaultDay - a single day in the default roster.
 */
export class RosterEmployeeDefaultDay extends BaseModel {
  dayLabel!: string;
  hour!: EmployeeHour;
  breaks!: EmployeeBreak[];

  static override relations(): RelationConfig {
    return {
      hour: { type: EmployeeHour },
      breaks: { type: EmployeeBreak },
    };
  }
}
